import { useCallback, useEffect, useRef, useState } from "react";
import type { JobListFilterViewModel } from "@models/job";

interface FilterUser {
  id?: string;
  email?: string;
}

const initialFilterFor = (
  user: FilterUser | undefined,
): JobListFilterViewModel => ({
  userId: user?.id ?? null,
  userEmail: user?.email ?? null,
  startDate: null,
  endDate: null,
  status: "all",
});

export const hasActiveFilter = (f: JobListFilterViewModel): boolean =>
  !!(f.userId || f.userEmail || f.startDate || f.endDate || f.status !== "all");

export interface UseJobListFilterResult {
  filter: JobListFilterViewModel;
  applyFilter: (next: JobListFilterViewModel) => void;
  isActive: boolean;
  popover: {
    open: boolean;
    anchorEl: HTMLElement | null;
    openAt: (event: React.MouseEvent<HTMLElement>) => void;
    close: () => void;
  };
}

export const useJobListFilter = (
  user: FilterUser | undefined,
): UseJobListFilterResult => {
  const [filter, setFilter] = useState<JobListFilterViewModel>(() =>
    initialFilterFor(user),
  );
  const seededRef = useRef<boolean>(!!user?.id);
  useEffect(() => {
    if (seededRef.current) return;
    if (user?.id) {
      seededRef.current = true;
      setFilter(initialFilterFor(user));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const applyFilter = useCallback((next: JobListFilterViewModel): void => {
    setFilter(next);
  }, []);

  const openAt = useCallback(
    (event: React.MouseEvent<HTMLElement>): void => {
      setAnchorEl(event.currentTarget);
      setOpen(true);
    },
    [],
  );

  const close = useCallback((): void => {
    setOpen(false);
    setAnchorEl(null);
  }, []);

  return {
    filter,
    applyFilter,
    isActive: hasActiveFilter(filter),
    popover: { open, anchorEl, openAt, close },
  };
};
